import React from 'react';
import Icon from '@/components/ui/AppIcon';

const ASSESSMENTS = [
  {
    id: 'assess-001',
    type: 'Formative',
    title: 'Daily Exit Tickets',
    description:
      'Short 2-question prompts at the end of each class session to check for understanding of key concepts. Teacher reviews before the next class to adjust instruction.',
    icon: 'PencilSquareIcon',
    color: 'bg-amber-50 border-amber-200',
    labelColor: 'bg-amber-100 text-amber-700',
  },
  {
    id: 'assess-002',
    type: 'Formative',
    title: 'Socratic Seminar Participation Tracker',
    description:
      'Teacher tracks student contributions during discussion using the participation grid. Students self-assess using the provided reflection rubric after the seminar.',
    icon: 'ClipboardDocumentCheckIcon',
    color: 'bg-blue-50 border-blue-200',
    labelColor: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'assess-003',
    type: 'Summative',
    title: 'Climate Justice Policy Brief',
    description:
      'Students write a 1-page evidence-based policy brief proposing a concrete climate justice action. Graded using the 4-point analytic rubric (included). Covers: claim, evidence, reasoning, and solution quality.',
    icon: 'DocumentTextIcon',
    color: 'bg-primary-50 border-primary-200',
    labelColor: 'bg-primary-100 text-primary-700',
  },
  {
    id: 'assess-004',
    type: 'Alternative',
    title: 'Visual Advocacy Poster',
    description:
      'Students create a poster or infographic advocating for climate justice using data from the unit. Alternative assessment option for students who demonstrate learning better through visual expression.',
    icon: 'PhotoIcon',
    color: 'bg-terracotta-50 border-terracotta-200',
    labelColor: 'bg-terracotta-100 text-terracotta-700',
  },
];

const RUBRIC_ROWS = [
  { criterion: 'Claim', excellent: 'Clear, specific, and debatable climate justice claim', proficient: 'Identifiable claim with some specificity', developing: 'Vague or general claim present', beginning: 'No clear claim' },
  { criterion: 'Evidence', excellent: '3+ pieces of relevant, credible evidence cited correctly', proficient: '2 pieces of relevant evidence', developing: '1 piece of evidence, weakly connected', beginning: 'No evidence or irrelevant evidence' },
  { criterion: 'Reasoning', excellent: 'Thorough explanation connecting evidence to claim and justice lens', proficient: 'Adequate explanation of connection', developing: 'Limited explanation; logic gaps present', beginning: 'No explanation of evidence connection' },
  { criterion: 'Solution', excellent: 'Specific, feasible solution with anticipated outcomes', proficient: 'Reasonable solution with some detail', developing: 'Vague solution, unclear feasibility', beginning: 'No solution proposed' },
];

export default function AssessmentTab() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-semibold text-stone-800 text-lg mb-5">Assessment Options</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ASSESSMENTS.map((a) => (
            <div
              key={a.id}
              className={`rounded-2xl border p-5 ${a.color}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0">
                  <Icon name={a.icon as any} size={18} className="text-stone-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.labelColor}`}>
                      {a.type}
                    </span>
                  </div>
                  <h3 className="font-semibold text-stone-800 text-sm mb-2">{a.title}</h3>
                  <p className="text-xs text-stone-600 leading-relaxed">{a.description}</p>
                  <button className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-stone-800 transition-colors">
                    <Icon name="ArrowDownTrayIcon" size={13} />
                    Download rubric
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rubric table */}
      <div>
        <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
          <Icon name="TableCellsIcon" size={18} className="text-primary-600" />
          Policy Brief Analytic Rubric
        </h3>
        <div className="overflow-x-auto rounded-2xl border border-stone-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                <th className="text-left px-4 py-3 font-semibold text-stone-700 w-28">Criterion</th>
                <th className="text-left px-4 py-3 font-semibold text-accent-700">Excellent (4)</th>
                <th className="text-left px-4 py-3 font-semibold text-primary-700">Proficient (3)</th>
                <th className="text-left px-4 py-3 font-semibold text-amber-700">Developing (2)</th>
                <th className="text-left px-4 py-3 font-semibold text-terracotta-700">Beginning (1)</th>
              </tr>
            </thead>
            <tbody>
              {RUBRIC_ROWS.map((row, i) => (
                <tr
                  key={`rubric-${row.criterion}`}
                  className={`border-b border-stone-100 ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}`}
                >
                  <td className="px-4 py-3 font-semibold text-stone-700">{row.criterion}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs leading-relaxed">{row.excellent}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs leading-relaxed">{row.proficient}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs leading-relaxed">{row.developing}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs leading-relaxed">{row.beginning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}